import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom'; // Import useLocation
import { useContext, useState, useEffect, useRef } from 'react'; // Add useState
import Home from './pages/Home.jsx';
import Chapter from './pages/Chapter.jsx';
import Register from './pages/register.jsx';
import Login from './pages/login.jsx';
import Profile from './pages/profile.jsx';
import Feedback from './pages/Feedback.jsx';
import Deliverables from './pages/Deliverables.jsx';
import logo from './assets/logo.svg';
import { AuthContext } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import AdminDashboard from './pages/AdminDashboard.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
// import Pricing from './pages/Pricing.jsx';
import EarlyAccess from './pages/EarlyAccess.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import CreatorProInfo from './pages/CreatorProInfo'; // Import the new page
import CreatorProTerms from './pages/CreatorProTerms'; // Import the new page
import Account from './pages/Account.jsx';
import MakerstateAgent from './components/MakerstateAgent';
import { SunIcon, MoonIcon } from '@heroicons/react/24/solid';

function AppContent() {
  const { isAuthenticated, authLoading, logout, user } = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null); // This ref will be for the mobile hamburger button
  const location = useLocation();
  const [currentVisibleBlockText, setCurrentVisibleBlockText] = useState(''); // New state for current block text
  const [currentBlockIndex, setCurrentBlockIndex] = useState(null); // New state for current block index
  const [currentFeedback, setCurrentFeedback] = useState(''); // Add feedback state

  const agentVisiblePaths = ['/profile', '/chapter', '/feedback', '/deliverables', '/admin'];
  const isAgentVisible = isAuthenticated && agentVisiblePaths.some(path => location.pathname.startsWith(path));

  // Check if the current page is a chapter page
  const isChapterPage = location.pathname.startsWith('/chapter/');

  useEffect(() => {
    const titles = {
      '/':            'Makerstate',
      '/profile':     'Home · Makerstate',
      '/deliverables':'Documents · Makerstate',
      '/admin':       'Admin · Makerstate',
      '/account':     'Account Settings · Makerstate',
      '/login':       'Log In · Makerstate',
      '/register':    'Sign Up · Makerstate',
      '/privacy':     'Privacy Policy · Makerstate',
      '/terms':       'Terms of Use · Makerstate',
      '/creator-academy': 'Creator Academy · Makerstate',
      '/early-access':    'Early Access · Makerstate',
    };
    const path = location.pathname;
    if (titles[path]) {
      document.title = titles[path];
    } else if (path.startsWith('/chapter/')) {
      document.title = 'Chapter · Makerstate';
    } else if (path.startsWith('/feedback/')) {
      document.title = 'Feedback · Makerstate';
    } else {
      document.title = 'Makerstate';
    }
  }, [location.pathname]);

  useEffect(() => {
    // Only scroll to top if there's no hash in the URL
    if (!location.hash) {
      window.scrollTo(0, 0);
    } else {
      // If there's a hash, let the browser handle the scroll naturally
      // or add a small delay to ensure the element exists
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }

    // Reset currentVisibleBlockText and currentBlockIndex when navigating away from a chapter page
    if (!location.pathname.startsWith('/chapter/')) {
      setCurrentVisibleBlockText('');
      setCurrentBlockIndex(null);
    }
    // Reset feedback when navigating away from feedback page
    if (!location.pathname.startsWith('/feedback/')) {
      setCurrentFeedback('');
    }
  }, [location.pathname, location.hash]); // Add location.hash to dependencies

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    }
    // Add event listener only if the menu can be opened
    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, buttonRef, isMobileMenuOpen]); // Simplified dependencies

  return (
    <>
      {/* Navbar - Remains full width. Agent is positioned below it. */}
      <nav className={`fixed top-0 left-0 w-full bg-white dark:bg-gray-900 z-[70] border-b border-gray-200 dark:border-gray-700`}>
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between relative">
          {/* Logo */}
          <Link to="/" className="text-xl flex items-center dark:text-white" onClick={() => setIsMobileMenuOpen(false)}>
            <img src={logo} alt="Logo" className="h-8 w-8 mr-2" />
            <span className="font-heading">Makerstate</span>
          </Link>

          {/* Desktop Navigation (md and up) */}
          <div className="hidden md:flex items-center space-x-6 font-body">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>
            {isAuthenticated ? (
              <>
                <NavLink 
                  to="/profile" 
                  className={({ isActive }) => `text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white ${isActive ? 'text-black dark:text-white font-semibold' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </NavLink>
                <NavLink 
                  to="/deliverables" 
                  className={({ isActive }) => `text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white ${isActive ? 'text-black dark:text-white font-semibold' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Documents
                </NavLink>
                {user?.role === 'admin' && (
                  <NavLink 
                    to="/admin" 
                    className={({ isActive }) => `text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white ${isActive ? 'text-black dark:text-white font-semibold' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Admin
                  </NavLink>
                )}
                {/* Desktop Hamburger Menu Trigger */}
                <div className="relative">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="main-menu"
                  >
                    <span className="sr-only">Open main menu</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16 M4 12h16 M4 18h16" />
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* <NavLink to="/pricing" className={({ isActive }) => `text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white ${isActive ? 'text-black dark:text-white font-semibold' : ''}`}>Pricing</NavLink> */}
                <NavLink to="/login" className={({ isActive }) => `rounded-md text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white ${isActive ? 'text-black dark:text-white font-semibold' : ''}`}>Log In</NavLink>
                <NavLink to="/register" className={({ isActive }) => `bg-black dark:bg-white text-white dark:text-black px-4 py-2 rounded-md font-medium ${isActive ? 'ring-2 ring-offset-2 ring-black dark:ring-white' : ''}`}>Sign Up</NavLink>
              </>
            )}
          </div>

          {/* Mobile Hamburger Button (visible below md) */}
          <div className="md:hidden flex items-center">
            <button
              ref={buttonRef} // Assign ref to the mobile hamburger button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={isMobileMenuOpen}
              aria-controls="main-menu"
            >
              <span className="sr-only">Open main menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16 M4 12h16 M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Dropdown Menu (Styled for mobile and desktop user menu) */}
        {isMobileMenuOpen && (
          <div
            ref={menuRef}
            id="main-menu"
            className={`
              font-body absolute bg-white focus:outline-none z-60 shadow-lg
              w-full top-16 left-0 right-0 rounded-none border-t border-gray-200 
              md:w-56 md:top-full md:right-4 md:left-auto md:mt-2 md:origin-top-right md:rounded-md md:border-none md:ring-1 md:ring-black md:ring-opacity-5
            `}
            role="menu" aria-orientation="vertical" aria-labelledby="user-menu-button" tabIndex="-1"
          >
            <div className="p-3" role="none">
              {isAuthenticated ? (
                <>
                  {/* Authenticated Links */}
                  <NavLink
                    to="/account"
                    className={({ isActive }) => `mb-2 rounded-md block px-4 py-2 text-sm ${isActive ? 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    role="menuitem" tabIndex="-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Account
                  </NavLink>
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="rounded-md block w-full text-left px-4 py-2 text-sm text-red-800 bg-pink-100 hover:bg-pink-200 hover:text-red-900"
                    role="menuitem" tabIndex="-1"
                  >
                    Logout
                  </button>
                </>
              ) : (
                // Unauthenticated Links (for mobile dropdown)
                <div className="md:hidden"> {/* Only show these in the mobile dropdown */}
                  {/* <NavLink
                    to="/pricing"
                    className={({ isActive }) => `mb-2 rounded-md block px-4 py-2 text-sm ${isActive ? 'bg-gray-200 text-black' : 'text-black hover:bg-gray-100'}`}
                    role="menuitem" tabIndex="-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Pricing
                  </NavLink> */}
                  <NavLink
                    to="/login"
                    className={({ isActive }) => `mb-2 rounded-md block px-4 py-2 text-sm ${isActive ? 'bg-gray-200 text-black' : 'text-black hover:bg-gray-100'}`}
                    role="menuitem" tabIndex="-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Log In
                  </NavLink>
                  <NavLink
                    to="/register"
                    className={({ isActive }) => `rounded-md block px-4 py-2 text-sm text-center ${isActive ? 'bg-gray-800 text-white' : 'bg-black text-white hover:bg-gray-800'}`}
                    role="menuitem" tabIndex="-1"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign Up
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Makerstate Agent Panel - Conditionally render and pass current block text, block index, and feedback */}
      {isAgentVisible && <MakerstateAgent currentContentBlockText={currentVisibleBlockText} currentBlockIndex={currentBlockIndex} currentFeedback={currentFeedback} />}

      {/* Main Routes - Conditionally add right margin and top padding */}
      <main className={`${!isChapterPage ? 'pt-16' : ''} min-h-screen dark:bg-gray-700 dark:text-white ${isAgentVisible ? 'mr-[33.3333%]' : ''}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Pass setCurrentVisibleBlockText and setCurrentBlockIndex to Chapter component */}
          <Route 
            path="/chapter/:id" 
            element={
              authLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : isAuthenticated === false ? (
                <Login />
              ) : (
                <Chapter setCurrentVisibleBlockText={setCurrentVisibleBlockText} setCurrentBlockIndex={setCurrentBlockIndex} isAgentVisible={isAgentVisible} />
              )
            } 
          />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          {/* <Route path="/pricing" element={<Pricing />} /> */}
          <Route path="/creator-academy" element={<CreatorProInfo />} />
          <Route path="/creator-pro-terms" element={<CreatorProTerms />} />
          <Route path="/early-access" element={<EarlyAccess />} />
          <Route 
            path="/profile" 
            element={
              authLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : isAuthenticated === false ? (
                <Login />
              ) : (
                <Profile />
              )
            } 
          />
          <Route 
            path="/feedback/:chapterId" 
            element={
              authLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : isAuthenticated === false ? (
                <Login />
              ) : (
                <Feedback setCurrentFeedback={setCurrentFeedback} />
              )
            } 
          />
          <Route
            path="/deliverables"
            element={
              authLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : isAuthenticated === false ? (
                <Login />
              ) : (
                <Deliverables />
              )
            }
          />
          <Route
            path="/admin"
            element={
              authLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : isAuthenticated === false ? (
                <Login />
              ) : isAuthenticated && user && user.role === 'admin' ? (
                <AdminDashboard />
              ) : isAuthenticated && !user ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading user data...</p>
                </div>
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/account"
            element={
              authLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                </div>
              ) : isAuthenticated === false ? (
                <Login />
              ) : (
                <Account />
              )
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </main>

      {/* Footer Section - Conditionally add right margin of 1/3 page width */}
      <footer className={`border-t-2 bg-black py-8 ${isAgentVisible ? 'mr-[33.3333%]' : ''}`}>
        <div className="container font-body mx-auto px-4 text-center text-white">
          <p>&copy; {new Date().getFullYear()} Chameleon Eye, LLC. All rights reserved.</p>
          <div className="mt-4">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-200 mx-2">Privacy Policy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-200 mx-2">Terms of Use</a>
          </div>
        </div>
      </footer>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
